import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import GoogleReviews from './components/GoogleReviews';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function TestimonialsPage() {
  const ctaRef = useReveal();
  const heroRef = useReveal(0);
  const imgLeftRef = useReveal(150);
  const imgRightRef = useReveal(150);

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="pt-28 sm:pt-40 pb-14 sm:pb-24 bg-white border-b border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex items-center justify-center gap-8 lg:gap-14">

            {/* Left Photo */}
            <div ref={imgLeftRef} className="reveal hidden lg:block flex-shrink-0 w-[220px] xl:w-[260px]">
              <div className="w-full h-[320px] xl:h-[380px] rounded-2xl overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/9caf5728-6755-4c3f-97ef-9162838cb2ba/e1d860f4-45ac-408e-b246-6c3dc0a522f8_IMG_3913.jpeg?v=2413a44c55b6f20d93565d69cf3ffeb0"
                  alt="Kendrick Taylor with clients"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Center Text */}
            <div ref={heroRef} className="reveal text-center flex-1 max-w-xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">
                Client Reviews
              </p>
              <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-[#111111] mb-6 animate-fade-in-up delay-200 leading-tight">
                Trusted by Buyers &amp; Sellers<br />
                <em className="not-italic font-semibold">Across Southeast Michigan</em>
              </h1>
              <p className="text-[#666] leading-relaxed animate-fade-in-up delay-300 max-w-md mx-auto text-sm">
                See why clients across Monroe, Lenawee, Washtenaw, and Wayne counties choose Kendrick Taylor for their most important moves.
              </p>
            </div>

            {/* Right Photo */}
            <div ref={imgRightRef} className="reveal hidden lg:block flex-shrink-0 w-[220px] xl:w-[260px]">
              <div className="w-full h-[320px] xl:h-[380px] rounded-2xl overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/9caf5728-6755-4c3f-97ef-9162838cb2ba/8a0c1651-8d3b-41fc-8e4c-ff016a96601f_IMG_4088.jpeg?v=6d79f1c5d508053cc45f044753d18df5"
                  alt="Kendrick Taylor real estate"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-[#111111]">
        <div ref={ctaRef} className="reveal max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-white mb-6">Ready to Work Together?</h2>
          <p className="text-white/70 mb-8 sm:mb-10">Join the clients who've made smarter, smoother moves with Kendrick.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 hover-lift">
              <i className="ri-phone-line"></i> Call Kendrick
            </a>
            <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap hover-lift">
              Send a Message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
